// The purpose of this store.
//
// This keeps track of the current state of options as we represent them in the UI, i.e. the modals the
// user interacts with to create a new import.  This is different than the back end represents them 
// because the UI is simpler than what the back end supports. That may change someday.
// 
// The values for defaults that come from the cookie reflect that back end model and are used to
// initiate these values but are basically unused from that point on.  The values in this store represent
// the current state of import options and will be saved between invocation, so if you use the modal once
// it will have the same settings when you come back to it. Within a single Redux invocation of course,
// that will reset if the browser is refreshed.

import { Reducer } from 'redux';

import { AccountingCollectionGroup } from '../../Models/AccountingCollectionGroup';
import { CookieSROtoPortalSRO, PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';
import { AttributedParagraphSet } from '../../Utils/AttributedParagraph';
import { StrongboxConnectionDescriptor } from '../../Utils/ConnectStrongbox';
import { LogMessage, SeverityLevel } from '../../Utils/Logging';
import { ReadOverriddenAppSettingsValues } from '../AppSettings';
import {
    AccountingSystemConnectionState,
    ExistingConnectionLookupState,
    ICurrentSubmission,
    KnownAction,
    NewDataCollectionParametersActions,
    SubmissionState
} from './Actions';


// In SelectImportDataItems, get rid of the stuff that defines this, i.e. CollectionGroupContent.
//
// Move all that logic here.

export type NewDataCollectionContentGroupingDescription = {
    description: string;
    bold: boolean;
    italic: boolean;
}

export type NewDataCollectionContentGrouping = {
    collectionGroup: AccountingCollectionGroup
    selected: boolean;
    description: NewDataCollectionContentGroupingDescription[];
}

export interface INewDataCollectionParametersState {
    retrieving: boolean;
    creationOptions: PortalCreateSubmissionRequestOptions | undefined;
    fiscalYearsToCollect: number;
    requestedOptions: PortalCreateSubmissionRequestOptions | undefined;
    requestedOptionsId: number | undefined;
    initializationException?: any;
    newDataCollectionGroupings: NewDataCollectionContentGrouping[];
    currentSubmission: null | ICurrentSubmission;
    dataCollectionSubmissionsCount: number;
    workspaceDescriptors: { [workspaceId: string]: StrongboxConnectionDescriptor }
}



export const defaultState: INewDataCollectionParametersState = {
    retrieving: false,
    creationOptions: undefined,
    requestedOptions: undefined,
    requestedOptionsId: undefined,
    fiscalYearsToCollect: 3,
    newDataCollectionGroupings: [],
    currentSubmission: null,
    dataCollectionSubmissionsCount: 0,
    workspaceDescriptors: {}
};

function MergeOverriddenCollectionGroups(
    appStateOptions?: PortalCreateSubmissionRequestOptions,
    overrideOptions?: PortalCreateSubmissionRequestOptions
): Map<string, AccountingCollectionGroup> {
    if (!appStateOptions) {
        // This is really a sanity check, shouldn't happen.  The result will be
        // alright if it occurs.
        return new Map<string, AccountingCollectionGroup>();
    }
    if (!overrideOptions) {
        return appStateOptions.accountingCollectionGroups;
    }

    const result = new Map<string, AccountingCollectionGroup>();
    appStateOptions.accountingCollectionGroups.forEach(group => {
        const temp = overrideOptions.accountingCollectionGroups.get(group.id);
        result.set(group.id, {
            ...group,
            ...temp
        });
    })

    return result;
}

function InitializeCollectionGroupings(
    collectionGroupDescriptions: AttributedParagraphSet,
    ourState: INewDataCollectionParametersState,
    dynamicTenantSelectionEnabled: boolean
): NewDataCollectionContentGrouping[] {
    const result: NewDataCollectionContentGrouping[] = [];

    let accountingCollectionGroups = ourState.creationOptions?.accountingCollectionGroups;

    const overriddenState = ReadOverriddenAppSettingsValues(dynamicTenantSelectionEnabled);
    if (!!overriddenState) {
        accountingCollectionGroups = MergeOverriddenCollectionGroups(ourState.creationOptions, overriddenState?.defaultPortalSubmissionOptions);
    }

    if (!accountingCollectionGroups) {
        const msg = ('Unexpected error, there are no known collection groups in InitializeCollectionGroupings');
        console.error(msg);
        LogMessage(msg, SeverityLevel.Error);
        return [];
    }

    // This should preserve the ordering that comes in, so ultimately whatever order the groups are defined as
    // in appsettings.json should be how they get presented.
    accountingCollectionGroups.forEach(group => {
        result.push({
            collectionGroup: {
                ...group
            },
            selected: group.collect,
            description: [],
        })
    });

    if (!collectionGroupDescriptions) {
        const msg = ('Warning, collectionGroupDescriptions is undefined in InitializeCollectionGroupings (this is normal immediately after startup)');
        console.warn(msg);
        LogMessage(msg, SeverityLevel.Error);
        return [];
    }

    // Go through each paragraph of description text.
    collectionGroupDescriptions.paragraphs.forEach(pg => {
        // Within each attributed paragraph there should be an id attribute.  If there isn't skip this paragraph it's invalid
        const attr = pg.attributes.find(a => a.name === 'id');
        if (!!attr) {
            // Now look through our possible values for content in result.  If we find the
            // id attribute already there use it, otherwise it was left out of the values that came up
            // from appsettings.json via cookie, ignore it.
            const key = result.find(c => c.collectionGroup.id === attr.value)
            if (!!key) {
                const isBold = !!pg.attributes.find(a => a.name === 'bold');
                const isItalic = !!pg.attributes.find(a => a.name === 'italic');

                key.description.push({ description: pg.content, bold: isBold, italic: isItalic });
            } else {
                const msg = `Ignoring collection grouping paragraph, id not found as a valid group descriptor in InitializeCollectionGroupings. ${attr.value}`;
                console.error(msg);
                console.error(pg.content);
                LogMessage(msg, SeverityLevel.Warning, { ...pg });
            }
        } else {
            const msg = `Unexpected error, expecting attributed paragraph to have 'id' attribute in InitializeCollectionGroupings.`;
            console.error(msg);
            console.error(pg.content);
            LogMessage(msg, SeverityLevel.Error, { ...pg });
        }
    });

    return result;
}

export const reducer: Reducer<INewDataCollectionParametersState, KnownAction> = (
    state: INewDataCollectionParametersState | undefined,
    action: KnownAction
): INewDataCollectionParametersState => {
    let newState: INewDataCollectionParametersState | undefined = undefined;

    switch (action.type) {
        case NewDataCollectionParametersActions.SetOptions: {
            newState = state ? { ...state } : { ...defaultState };

            let groups = new Map<string, AccountingCollectionGroup>();

            if (!!(newState.creationOptions?.accountingCollectionGroups)) {
                groups = new Map<string, AccountingCollectionGroup>(newState.creationOptions.accountingCollectionGroups);
            }

            newState.creationOptions = {
                ...action.options,
                metadata: action.options.metadata?.slice() || undefined,
            };
            newState.creationOptions.accountingCollectionGroups = groups;

            newState.fiscalYearsToCollect = action.fiscalYearsToCollect;

            break;
        }
        case NewDataCollectionParametersActions.Initialize: {
            newState = state ? { ...state } : { ...defaultState };

            const overriddenState = ReadOverriddenAppSettingsValues(action.appData.dynamicTenantSelection);

            if (!!overriddenState) {
                newState.creationOptions = overriddenState.defaultPortalSubmissionOptions;
            } else {
                newState.creationOptions = CookieSROtoPortalSRO(action.appData.defaultPortalSubmissionOptions);
            }

            break;
        }
        case NewDataCollectionParametersActions.InitializeCollectionGroupings: {
            newState = state ? { ...state } : { ...defaultState };
            newState.newDataCollectionGroupings = InitializeCollectionGroupings(action.collectionGroupDescriptions, newState, action.dynamicTenantSelectionEnabled);
            break;
        }
        case NewDataCollectionParametersActions.BeginInitializeFromURL: {
            newState = state ? { ...state } : { ...defaultState };

            newState.retrieving = true;
            break;
        }
        case NewDataCollectionParametersActions.EndInitializeFromURL: {
            newState = state ? { ...state } : { ...defaultState };

            newState.retrieving = false;
            newState.requestedOptions = undefined;
            newState.requestedOptionsId = action.optionsId;
            newState.initializationException = action.initializationException;

            if (action.options) {
                newState.requestedOptions = {
                    ...action.options,
                }
            }

            break;
        }
        case NewDataCollectionParametersActions.BeginCurrentSubmissionRequest: {
            newState = state ? { ...state } : { ...defaultState };

            // The only action that (re)builds the currentSubmission object.
            newState.currentSubmission = {
                dataCollectionSubmissionRequest: action.dataCollectionSubmissionRequest,
                submissionRequest: null,
                submissionState: SubmissionState.creatingRequest,
                submission: null,
                accountingPackage: null,
                accountingSystemConnectionState: AccountingSystemConnectionState.idle,
                connectionError: undefined,
                existingConnectionLookupState: ExistingConnectionLookupState.pending,
                existingConnection: null,
                connectEndpoint: null,
            };

            break;
        }
        case NewDataCollectionParametersActions.SetSubmissionRequestCreated: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error('Cannot set the created submission request without a currentSubmission');
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                submissionRequest: action.submissionRequest,
                submissionState: SubmissionState.requestCreated,
            };
            break;
        }
        case NewDataCollectionParametersActions.SetSubmissionRequestError: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error('Cannot set submission request error without a currentSubmission');
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                submissionState: SubmissionState.requestError,
            };
            break;
        }
        case NewDataCollectionParametersActions.SetCreatingSubmission: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error('Cannot transition to creatingSubmission without a currentSubmission');
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                submissionState: SubmissionState.creatingSubmission,
            };
            break;
        }
        case NewDataCollectionParametersActions.SetSubmissionError: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error('Cannot set submission error without a currentSubmission');
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                submissionState: SubmissionState.submissionError,
            };
            break;
        }
        case NewDataCollectionParametersActions.ClearCurrentSubmission: {
            newState = state ? { ...state } : { ...defaultState };

            newState.currentSubmission = null;
            break;
        }
        case NewDataCollectionParametersActions.CompleteCurrentSubmission: {
            newState = state ? { ...state } : { ...defaultState };

            if (!!newState.currentSubmission) {
                newState.dataCollectionSubmissionsCount += 1;
            }
            newState.currentSubmission = null;
            break;
        }
        case NewDataCollectionParametersActions.SetSelectedAccountingPackage: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error("Should not be setting an accounting package before creating a submission request");
            }
            if (newState.currentSubmission.submissionState === SubmissionState.creatingSubmission) {
                throw new Error("Should not be setting an accounting package before while in the middle of creating a submission");
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                accountingPackage: action.accountingPackage,
                // Reset on every (re)selection so the lookup re-runs for the new package; the flow view
                // shows the "working" spinner until SetExistingConnection resolves it.
                existingConnectionLookupState: ExistingConnectionLookupState.pending,
                existingConnection: null,
                // reset the submission state back to request created. Once the accounting system changes 
                // we need to create a new submission 
                submissionState: SubmissionState.requestCreated,
                submission: null,
            };
            break;
        }
        case NewDataCollectionParametersActions.SetCurrentSubmission: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission || newState.currentSubmission.submissionState !== SubmissionState.creatingSubmission) {
                throw new Error("Should not be setting the submission before creating a submission request");
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                submissionState: SubmissionState.submissionCreated,
                submission: { id: action.submissionId },
            };
            break;
        }
        case NewDataCollectionParametersActions.SetConnectionDescriptor: {
            newState = state ? { ...state } : { ...defaultState };

            const workspaceDescriptors = { ...newState.workspaceDescriptors };
            Object.keys(workspaceDescriptors).forEach(workspaceId => {
                if (workspaceId === action.descriptor.orgId) {
                    workspaceDescriptors[workspaceId] = action.descriptor;
                } else {
                    const descriptor = workspaceDescriptors[workspaceId];
                    if (new Date(descriptor.delegatedAccessToken.expiration) >= new Date()) {
                        delete workspaceDescriptors[workspaceId];
                    }
                }
            });
            newState.workspaceDescriptors = workspaceDescriptors;
            break;
        }
        case NewDataCollectionParametersActions.SetConnectEndpoint: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error('Cannot set the connect endpoint without a currentSubmission');
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                connectEndpoint: action.connectEndpoint,
            };
            break;
        }
        case NewDataCollectionParametersActions.SetAccountingSystemConnectionState: {
            newState = state ? { ...state } : { ...defaultState };

            if (!newState.currentSubmission) {
                throw new Error('Cannot set accounting system connection state without a currentSubmission');
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                accountingSystemConnectionState: action.connectionState,
                connectionError: action.connectionError,
            };
            break;
        }
        case NewDataCollectionParametersActions.SetExistingConnection: {
            newState = state ? { ...state } : { ...defaultState };

            // Discard stale results: if there is no submission, or the user has since selected a different
            // package, the lookup this result came from is superseded by a newer queued lookup. Drop it
            // silently (no throw) — a superseded lookup resolving late is expected, not an error.
            if (!newState.currentSubmission || newState.currentSubmission.accountingPackage !== action.accountingPackage) {
                break;
            }
            newState.currentSubmission = {
                ...newState.currentSubmission,
                existingConnectionLookupState: action.connection !== null ? ExistingConnectionLookupState.found : ExistingConnectionLookupState.notFound,
                existingConnection: action.connection,
            };
            break;
        }
    }

    if (newState) {
        return newState;
    } else if (state) {
        return state;
    } else {
        const defaultCopy: INewDataCollectionParametersState = {
            ...defaultState,
        };
        return defaultCopy;
    }
}
